// app/(app)/inventory/stock/add.tsx
import { Button, Input } from "@/components/default/forms";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { inventoryApi } from "@/services/inventoryApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchProducts } from "@/store/slices/productSlice";
import { clearError, createStockEntry } from "@/store/slices/stockSlice";
import { showAlert } from "@/utils/alert";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function AddStockScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { updating, error } = useAppSelector((state) => state.stock);
  const { products } = useAppSelector((state) => state.products);

  const [productId, setProductId] = useState<string>("");
  const [type, setType] = useState<"in" | "out">("in");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [currentStock, setCurrentStock] = useState<number>(0);
  const [loadingStock, setLoadingStock] = useState(false);
  const [errors, setErrors] = useState({
    productId: "",
    quantity: "",
    reason: "",
  });

  // Fetch products on mount
  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 100, search: "" }));
  }, []);

  // Fetch current stock when product changes
  useEffect(() => {
    if (!productId) {
      setCurrentStock(0);
      return;
    }
    const fetchStock = async () => {
      setLoadingStock(true);
      try {
        const balance = await inventoryApi.getStockByProduct(
          parseInt(productId),
        );
        setCurrentStock(balance.currentStock);
      } catch (err) {
        console.error(err);
        setCurrentStock(0);
      } finally {
        setLoadingStock(false);
      }
    };
    fetchStock();
  }, [productId]);

  // Reset form on screen focus
  useFocusEffect(
    useCallback(() => {
      setProductId("");
      setType("in");
      setQuantity("");
      setReason("");
      setCurrentStock(0);
      setErrors({ productId: "", quantity: "", reason: "" });
      dispatch(clearError());
    }, []),
  );

  useEffect(() => {
    if (error) showAlert("Error", error);
  }, [error]);

  // Compute future stock after movement
  const getFutureStock = () => {
    const qty = parseInt(quantity);
    if (isNaN(qty) || qty <= 0) return currentStock;
    if (type === "in") return currentStock + qty;
    return currentStock - qty;
  };

  const validate = () => {
    let valid = true;
    const newErrors = { productId: "", quantity: "", reason: "" };
    if (!productId) newErrors.productId = "Select product";
    const qty = parseInt(quantity);
    if (!quantity) newErrors.quantity = "Required";
    else if (isNaN(qty) || qty <= 0) newErrors.quantity = "Must be > 0";
    else if (type === "out" && qty > currentStock) {
      newErrors.quantity = `Only ${currentStock} available`;
    }
    if (!reason.trim()) newErrors.reason = "Required";
    setErrors(newErrors);
    return !Object.values(newErrors).some((e) => e);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const qty = parseInt(quantity);
    const result = await dispatch(
      createStockEntry({
        productId: parseInt(productId),
        type,
        quantity: qty,
        reason,
      }),
    );
    if (createStockEntry.fulfilled.match(result)) {
      showAlert("Success", "Stock movement recorded", [
        { text: "OK", onPress: () => router.push("/(app)/inventory/stock") },
      ]);
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.lg,
    },
    inputContainer: { marginBottom: spacing.lg },
    stockInfo: {
      flexDirection: "row",
      justifyContent: "space-between",
      backgroundColor: colors.border + "30",
      padding: spacing.md,
      borderRadius: 8,
      marginBottom: spacing.md,
    },
    stockLabel: { fontSize: 14, color: colors.textLight },
    stockValue: { fontSize: 16, fontWeight: "600", color: colors.text },
    typeRow: {
      flexDirection: "row",
      justifyContent: "space-around",
      marginBottom: spacing.lg,
    },
    typeButton: {
      flex: 1,
      paddingVertical: spacing.sm,
      marginHorizontal: spacing.sm,
      borderRadius: 8,
      alignItems: "center",
      borderWidth: 1,
      borderColor: colors.border,
    },
    activeType: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    typeText: { color: colors.text },
    activeTypeText: { color: "#fff" },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          style={styles.container}
          contentContainerStyle={styles.scrollContent}
        >
          <Text style={styles.title}>Add Stock Movement</Text>

          <SearchableSelect
            label="Product"
            placeholder="Select product"
            options={products.map((p) => ({
              label: `${p.name} (${p.sku})/${p.packQuantity}${p.Unit?.symbol}`,
              value: p.id.toString(),
            }))}
            value={productId}
            onValueChange={setProductId}
            error={errors.productId}
            containerStyle={styles.inputContainer}
          />

          {productId && (
            <View style={styles.stockInfo}>
              <Text style={styles.stockLabel}>Current Stock:</Text>
              <Text style={styles.stockValue}>
                {loadingStock ? "Loading..." : `${currentStock} units`}
              </Text>
            </View>
          )}

          <View style={styles.typeRow}>
            <TouchableOpacity
              style={[styles.typeButton, type === "in" && styles.activeType]}
              onPress={() => setType("in")}
            >
              <Text
                style={[
                  styles.typeText,
                  type === "in" && styles.activeTypeText,
                ]}
              >
                Stock IN
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.typeButton, type === "out" && styles.activeType]}
              onPress={() => setType("out")}
            >
              <Text
                style={[
                  styles.typeText,
                  type === "out" && styles.activeTypeText,
                ]}
              >
                Stock OUT
              </Text>
            </TouchableOpacity>
          </View>

          <Input
            label="Quantity"
            value={quantity}
            onChangeText={setQuantity}
            keyboardType="numeric"
            error={errors.quantity}
            containerStyle={styles.inputContainer}
          />

          {productId && quantity && !loadingStock && (
            <View style={styles.stockInfo}>
              <Text style={styles.stockLabel}>
                Future Stock (after {type === "in" ? "IN" : "OUT"}):
              </Text>
              <Text
                style={[
                  styles.stockValue,
                  {
                    color:
                      getFutureStock() < 0 ? colors.danger : colors.success,
                  },
                ]}
              >
                {getFutureStock()} units
              </Text>
            </View>
          )}

          <Input
            label="Reason"
            value={reason}
            onChangeText={setReason}
            error={errors.reason}
            containerStyle={styles.inputContainer}
          />

          <Button
            title="Record Movement"
            onPress={handleSubmit}
            loading={updating}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
